import { RateReview } from '@mui/icons-material';
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { FC, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { convertYYYYMMDDToDate } from '../../../util';
import { usePrompt } from '../../../Views';
import { NumberValidationIndividualDto, NumberValidationMultipleDto, OrderReviewDto, TicketEntryHaulerDto } from '../../../dtos';
import { useArchiveOrderReviewDeleteFromDatabaseByIdMutation, useCreateOrderReviewValidateFreightBillingNumbersMutation, useGetBrokerQuery, useGetDriverQuery, useLazyGetDispatchOrderManagementCurrentMonthQuery, useLazyGetOrderReviewCurrentMonthOrdersQuery } from '../../../store/generated/generatedApi';
import { DateScroller } from '../../CommonInputs';
import { HaulerSelect } from '../../CommonInputs/HaulerSelect';
import { SlimFormHeader } from '../../Core/SlimFormHeader';
import { IFormProps, UserPermissionContext, useStandardSnackbar, userHasPermissions } from '../../CoreLib/library';
import { DispatchTicketEntryTable } from './DispatchTicketEntryTable';
import { useTicketEntryForm } from './useTicketEntryForm';
import { format, isValid, startOfDay } from 'date-fns';

export interface ITicketFormProps extends IFormProps<OrderReviewDto> {
    dateGiven?: Date;
    setUpdatedDate: (date: string) => void;
}

export const TicketForm: FC<ITicketFormProps> = (props) => {
    const { permissions } = useContext(UserPermissionContext);
    const isUserAllowedToReview = userHasPermissions(['create:review', 'edit:review'], permissions, 'or');
    const isUserAllowedToBill = userHasPermissions(['create:billing', 'edit:billing'], permissions, 'or');

    const [getCurrentMonthOrders, { data: currentMonthOrders, isFetching: isFetchingCurrentMonthOrders }] = useLazyGetOrderReviewCurrentMonthOrdersQuery();
    const [getCurrentMonthDispatch, { data: currentMonthDispatches, isFetching: isFetchingCurrentMonthDispatches }] = useLazyGetDispatchOrderManagementCurrentMonthQuery();
    const [validateFreightBillingNumbers] = useCreateOrderReviewValidateFreightBillingNumbersMutation();

    const [daysWithData, setDaysWithData] = useState<string[]>([]);

    const [searchParams, setSearchParams] = useSearchParams();
    const dateParam = searchParams.get('date');

    const {
        isFormDirty,
        handleSave,
        handleCancel,
        dispatchErrors,
        freightErrors,
        handleAddNewFreightBillingLineItem,
        handleRemoveFreightBillingLineItem,
        handleDispatchesChange,
        formDispatchReviews,
        getOrderById,
        handleAddNewDispatchReviewForOrderReviewLineItem,
        isLoadingDispatches,
        setCurrentDate: setDate,
        currentDate: date,
        formHauler,
        setFormHauler,
        isLoadingOrders
    } = useTicketEntryForm(props);
    const [month, setMonth] = useState<Date | undefined>();

    useEffect(() => {
        setDate(dateParam ? convertYYYYMMDDToDate(dateParam) : new Date());
        setMonth(dateParam ? convertYYYYMMDDToDate(dateParam) : new Date());
    }, [dateParam, setDate]);

    const loadData = useCallback(
        (date: Date) => {
            const formattedDate = format(date, 'yyyy-MM');
            getCurrentMonthDispatch({ startDate: formattedDate });
            getCurrentMonthOrders({ startDate: formattedDate });
        },
        [getCurrentMonthDispatch, getCurrentMonthOrders]
    );

    useEffect(() => {
        if (month) {
            loadData(month);
        }
    }, [month, loadData]);

    useEffect(() => {
        if (!isFetchingCurrentMonthOrders && !isFetchingCurrentMonthDispatches) {
            const currentDaysFilter = [...(currentMonthDispatches ?? []), ...(currentMonthOrders ?? [])];
            setDaysWithData(currentDaysFilter);
        }
    }, [currentMonthDispatches, currentMonthOrders, isFetchingCurrentMonthOrders, isFetchingCurrentMonthDispatches]);

    const onMonthChange = useCallback((newMonth: Date) => {
        setDaysWithData([]);
        setMonth(newMonth);
    }, [setDaysWithData, setMonth]);

    usePrompt('Are you sure you want to leave this page?\nUnsaved changes will be lost.', isFormDirty());


    useEffect(() => {
        if (date) {
            props.setUpdatedDate(format(date, 'yyyy-MM-dd'));
        }
    }, [date, props]);

    const { isFetching: loadingBrokers, data: brokers } = useGetBrokerQuery({ searchText: '', sortKey: 'CODE', page: 0, pageSize: 100000, sortAsc: true, includeInactive: false });
    const { isFetching: loadingDrivers, data: drivers } = useGetDriverQuery({ searchText: '', sortKey: 'CODE', page: 0, pageSize: 100000, sortAsc: true, includeInactive: false });

    const editDisabled = useMemo(() => {
        if (loadingBrokers || loadingDrivers || isLoadingDispatches || isLoadingOrders) {
            return true;
        }
        if (isUserAllowedToBill && isUserAllowedToReview) {
            return false;
        }
        return true;
    }, [isUserAllowedToBill, isUserAllowedToReview, loadingBrokers, loadingDrivers, isLoadingDispatches, isLoadingOrders]);

    const updateDate = useCallback(
        (updatedDate: Date) => {
            const newDate = new Date(updatedDate);

            if (isValid(newDate)) {
                setDate(startOfDay(newDate));
                setSearchParams(createSearchParams({ date: format(newDate, 'yyyy-MM-dd') }));
            }
        },
        [setSearchParams, setDate]
    );

    const handleHaulerChange = useCallback((selectedHauler?: TicketEntryHaulerDto) => {
        setFormHauler(selectedHauler);
    }, [setFormHauler]);

    const [isFreightBillNumberUniqueLocallyError, setIsFreightBillNumberUniqueLocallyError] = useState(false);
    const [isFreightBillNumberUniqueGloballyError, setIsFreightBillNumberUniqueGloballyError] = useState(false);
    const [duplicateFreightBillNumber, setDuplicateFreightBillNumber] = useState<string | undefined>();

    useStandardSnackbar(isFreightBillNumberUniqueLocallyError, "Warning: Duplicate Freight Bill Number. (# " + (duplicateFreightBillNumber ? duplicateFreightBillNumber : '') + " already exists for this Hauler on this day). Please validate the ticket number.", 'warning', () => {
        setIsFreightBillNumberUniqueLocallyError(false);
        setDuplicateFreightBillNumber(undefined);
    }, { autoHideDuration: 5000 });
    useStandardSnackbar(isFreightBillNumberUniqueGloballyError, "Warning: Duplicate Freight Bill Number. (# " + (duplicateFreightBillNumber ? duplicateFreightBillNumber : '') + " already exists). Please validate the ticket number.", 'warning', () => {
        setIsFreightBillNumberUniqueGloballyError(false);
        setDuplicateFreightBillNumber(undefined);
    }, { autoHideDuration: 5000 });

    const handleUpdatedFreightBillingNumber = useCallback(async (updatedFreightBillingNumber: string, dispatchIndex: number, freightBillingLineItemIndex: number) => {
        const formFreightBillingNumbers = formDispatchReviews.flatMap((dispatchReview, dispatchIdx) =>
            dispatchReview.freightBillingLineItems?.map((freightItem, freightIdx) => 
                dispatchIdx === dispatchIndex && 
                freightIdx === freightBillingLineItemIndex 
                    ? updatedFreightBillingNumber   
                    : freightItem.freightBillNumber
            )
        );

        if (updatedFreightBillingNumber === '') {
            return; // If the user deletes the freight bill number, we don't need to check for uniqueness.
        }

        if (formFreightBillingNumbers.filter((item) => item === updatedFreightBillingNumber).length > 1) {
            setIsFreightBillNumberUniqueLocallyError(true);
            setDuplicateFreightBillNumber(updatedFreightBillingNumber);
        } else {
            const numbersToValidate: NumberValidationMultipleDto = {
                numberValidations: [
                    {
                        number: updatedFreightBillingNumber,
                        isValid: false
                    }
                ] as NumberValidationIndividualDto[]
            };
            const response = await validateFreightBillingNumbers(numbersToValidate).unwrap();
            if (!response.numberValidations.find((item) => item.number === updatedFreightBillingNumber)?.isValid) {
                setIsFreightBillNumberUniqueGloballyError(true);
                setDuplicateFreightBillNumber(updatedFreightBillingNumber);
            } else {
                setIsFreightBillNumberUniqueGloballyError(false);
                setIsFreightBillNumberUniqueLocallyError(false);
                setDuplicateFreightBillNumber(undefined);
            }
        }
    }, [formDispatchReviews, validateFreightBillingNumbers]);

    const isLoading = useMemo(() => {
        return isLoadingDispatches || isFetchingCurrentMonthOrders || isFetchingCurrentMonthDispatches || loadingBrokers || loadingDrivers || isLoadingOrders;
    }, [isLoadingDispatches, isFetchingCurrentMonthOrders, isFetchingCurrentMonthDispatches, loadingBrokers, loadingDrivers, isLoadingOrders]);

    const dispatchDetails = useCallback(() => {
        if (!date) {
            return null;
        }
        return (
            <Box sx={{ width: '100%', borderBottom: 1, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                <Box flexGrow={1} overflow='hidden'>
                    <DispatchTicketEntryTable
                        brokers={brokers?.pageResults ?? []}
                        disabled={editDisabled}
                        dispatchReviews={formDispatchReviews}
                        drivers={drivers?.pageResults ?? []}
                        setDispatches={handleDispatchesChange}
                        addFreightBillingLineItem={handleAddNewFreightBillingLineItem}
                        removeFreightBillingLineItem={handleRemoveFreightBillingLineItem}
                        handleUpdatedFreightBillingNumber={handleUpdatedFreightBillingNumber}
                        dispatchErrors={dispatchErrors}
                        freightErrors={freightErrors}
                        getOrderById={getOrderById}
                        date={date}
                        addDispatchReviewForOrderReviewLineItem={(orderReview, orderReviewLineItem) => {
                            handleAddNewDispatchReviewForOrderReviewLineItem(formHauler!, orderReview, orderReviewLineItem);
                        }}
                        disableAdd={editDisabled || !formHauler}
                    />
                </Box>
            </Box>
        );
    }, [formHauler, brokers?.pageResults, date, dispatchErrors, drivers?.pageResults, editDisabled, formDispatchReviews, freightErrors, getOrderById, handleAddNewFreightBillingLineItem, handleRemoveFreightBillingLineItem, handleDispatchesChange, handleAddNewDispatchReviewForOrderReviewLineItem, handleUpdatedFreightBillingNumber]);

    const ticketForm = useMemo(() => {
        if (!date) {
            return null;
        }
        return (
            <Box display='flex' flexDirection='column' height='100%' overflow='hidden'>
                <>
                    <SlimFormHeader
                        objectName='Order'
                        permissionsTypeName='order'
                        icon={<RateReview />}
                        title={`Enter Ticket`}
                        breadcrumbs={[
                            { label: 'Home', navLink: '/' },
                            { label: 'Review', navLink: `/orderReview?date=${format((date ? new Date(date) : new Date()), 'yyyy-MM-dd')} ` },
                        ]}
                        isFormDirty={isFormDirty}
                        handleCancel={handleCancel}
                        handleSave={handleSave}
                        entityNameSingular={'order'}
                        hideDividers
                        deleteConfirmationDialog={{ title: 'Delete Ticket?', content: <Typography>Are you sure you would like to permanently delete this record? It will be deleted immediately and cannot be recovered.</Typography> }}
                        deleteMutation={useArchiveOrderReviewDeleteFromDatabaseByIdMutation}
                        
                        additionalHeaderItems={
                            <DateScroller
                                date={date}
                                updateDate={updateDate}
                                fullWidth
                                daysWithData={daysWithData}
                                handleOnMonthChange={onMonthChange}
                            />
                        }
                        additionalItemsOnRight={true}
                    />
                    <Paper sx={{ height: '100%', display: 'flex', flexGrow: 1, overflow: 'hidden', flexDirection: 'column', p: 2, m: 2 }}>
                        <Box display='flex' flexDirection='column' height='100%' overflow='hidden'>
                            <Grid container justifyContent="center" alignItems="center">
                                <Grid item xs={8} sm={6} md={4}>
                                    {isFormDirty() && (
                                        <Typography variant='body2' color='error'>
                                            There are unsaved changes. Please save your changes or cancel before selecting a new Hauler.
                                        </Typography>
                                    )}
                                    <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                                        <div style={{ width: '400px' }}>
                                            <HaulerSelect
                                                selectedHauler={formHauler ?? null}
                                                handleSelectedHaulerChange={handleHaulerChange}
                                                required
                                                disabled={isFormDirty()}
                                                date={date}
                                            />
                                        </div>
                                        <div style={{ marginTop: '25px' }}>
                                            {isLoading ? (
                                                <CircularProgress size={'32px'} />
                                            ) : (
                                                <div></div>
                                            )}
                                        </div>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Box>
                                <Typography variant='h2' mt={1}>Dispatches</Typography>
                            </Box>
                            <Box display='flex' flexDirection='column' height='100%' overflow='hidden'>
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'auto' }}>
                                    {dispatchDetails()}
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </>
            </Box>
        );
    }, [isLoading, date, dispatchDetails, formHauler, handleCancel, handleHaulerChange, handleSave, isFormDirty, updateDate, daysWithData, onMonthChange]);

    return (
        <>{ticketForm}</>
    );
};
